/**
 * outlook-context — reads the currently open message/appointment (read OR
 * compose mode) and exposes it as a HostContextProvider so the assistant can
 * answer about / act on "this email" without the user pasting it. Outlook is a
 * mailbox host: subject/from are plain values in read mode but async getters in
 * compose mode; the body is read via getAsync in both modes (mirrors the
 * promisified pattern in outlook-tools.ts).
 */
import { HostContextProvider, capText } from "@usable/office-embed";

const MAX_CONTEXT_CHARS = 4000;

/** A field that is either a plain string (read mode) or an async getter (compose). */
type MaybeAsyncString = string | { getAsync(cb: (r: Office.AsyncResult<string>) => void): void };

interface MinimalMailboxItem {
  itemType?: string;
  subject?: MaybeAsyncString;
  from?: { displayName?: string; emailAddress?: string };
  body: { getAsync(coercion: Office.CoercionType, cb: (r: Office.AsyncResult<string>) => void): void };
}

function readMaybeAsyncString(field: MaybeAsyncString | undefined): Promise<string> {
  if (typeof field === "string") return Promise.resolve(field);
  if (field && typeof field.getAsync === "function") {
    return new Promise((resolve) => {
      field.getAsync((r) =>
        resolve(r.status === Office.AsyncResultStatus.Succeeded ? String(r.value ?? "") : "")
      );
    });
  }
  return Promise.resolve("");
}

async function read(): Promise<string | null> {
  const item = Office.context.mailbox?.item as unknown as MinimalMailboxItem | undefined;
  if (!item) return null;
  try {
    const isAppointment = item.itemType === "appointment";
    const lines = [
      `The user is working in Microsoft Outlook (${isAppointment ? "appointment" : "email"}).`,
    ];

    const subject = await readMaybeAsyncString(item.subject);
    if (subject) lines.push(`Subject: ${subject}`);

    // `from` is a plain value in read mode; absent/irrelevant in compose.
    if (item.from?.emailAddress) {
      lines.push(`From: ${item.from.displayName ?? ""} <${item.from.emailAddress}>`);
    }

    const body = await new Promise<string>((resolve) => {
      try {
        item.body.getAsync(Office.CoercionType.Text, (r) =>
          resolve(r.status === Office.AsyncResultStatus.Succeeded ? r.value ?? "" : "")
        );
      } catch {
        resolve("");
      }
    });
    if (body.trim()) lines.push("Body:", body.trim());

    return capText(lines.join("\n"), MAX_CONTEXT_CHARS);
  } catch {
    return null;
  }
}

function subscribe(onChange: () => void): () => void {
  const mailbox = Office.context.mailbox;
  if (!mailbox?.addHandlerAsync) return () => {};
  // ItemChanged fires when the user switches to a different message/appointment.
  mailbox.addHandlerAsync(Office.EventType.ItemChanged, onChange);
  return () => {
    try {
      // Mailbox removeHandlerAsync has no per-handler removal; we only ever
      // register this one ItemChanged handler, so clearing the event is fine.
      mailbox.removeHandlerAsync?.(Office.EventType.ItemChanged);
    } catch {
      // best-effort cleanup
    }
  };
}

export const outlookContextProvider: HostContextProvider = { read, subscribe };
